import * as React from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import {
    Bloodwork,
    BloodworkNote,
    Section,
    BloodworkAnalysis,
    FurtherConsiderations,
    PanelMarker,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import {
    SectionNotesDigest,
    buildSectionDigest,
    digestTabToMarkdown,
    DigestTab,
} from './section-notes-digest';
import {
    MarkdownToolbar,
    renderNoteBody,
    wrapSelection,
    formatMarkerForKeydown,
    FormatMarker,
} from './note-markdown';
import {
    trianglesToArrows,
    arrowsToTriangles,
} from './further-considerations-section';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { isSameBloodwork } from '../../utils/sameBloodwork';

type Props = {
    section: Section;
    title: string;
    bloodwork: Bloodwork;
    bloodworkEntryStore: BloodworkEntryStore;
    // When these are provided (the per-section Notes tab), a "gather from tabs"
    // digest renders below the note, in preview and edit mode alike. Omitted for
    // the global Insights note, which has no single section to summarise, so the
    // digest simply doesn't appear there.
    bloodworkAnalysis?: BloodworkAnalysis;
    furtherConsiderations?: FurtherConsiderations;
    panelMarkers?: PanelMarker[];
    // Element id to scroll back to after Save (the enclosing card, so its
    // header stays in view). Without it the field's own top is used.
    scrollAnchorId?: string;
};

type State = {
    bloodworkNote: BloodworkNote;
    // View/edit mirrors the consolidated Section Notes panel: the saved note is
    // shown FORMATTED (headings, bold/italic/underline, arrows) until Edit is
    // clicked, so the practitioner sees how their markup reads without
    // scrolling down to the Section Notes summary.
    editing: boolean;
};

export class BloodworkNotes extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const bloodworkNote = this.initBloodworkNote(props.section, props.bloodwork);
        this.state = {
            bloodworkNote,
            // An empty note has nothing to preview, so open straight into the
            // editor rather than showing a blank box behind an Edit button.
            editing: !(bloodworkNote.text ?? '').trim(),
        };
    }

    private wrapperEl: HTMLDivElement | null = null;
    // The whole field (preview or editor) — the scroll anchor after a save.
    private rootEl: HTMLDivElement | null = null;
    private static MIN_HEIGHT_PX = 156;

    componentDidMount() {
        this.fitToContent();
    }

    componentWillReceiveProps(nextProps) {
        // Re-load from the saved model when the SECTION changes, or when the
        // panel is pointed at a DIFFERENT test. The analysis screen keeps one
        // BloodworkTestAnalysis mounted and swaps its bloodwork prop when the
        // practitioner clicks Analyze on another row, so without the second
        // check this panel would keep showing the previous test's note — and
        // Save would write that note over the new test's own.
        //
        // Re-loading on EVERY prop change is what we're still avoiding: unsaved
        // typing lives in local state, and any unrelated ancestor re-render
        // (a profile save, re-saving this same test) would otherwise wipe it.
        if (
            nextProps.section === this.props.section &&
            isSameBloodwork(nextProps.bloodwork, this.props.bloodwork)
        ) {
            return;
        }
        const { section, bloodwork } = nextProps;
        const bloodworkNote = this.initBloodworkNote(section, bloodwork);
        this.setState({
            bloodworkNote,
            editing: !(bloodworkNote.text ?? '').trim(),
        }, this.fitToContent);
    }

    // Notes gathered before Insert switched to ↑ / ↓ still hold the DB's ▲ / ▼.
    // They already DISPLAY as arrows (renderArrows covers both), but the editor
    // shows the raw stored text, so an old note would sit here full of triangles
    // while anything newly inserted used arrows. Normalise into local state on
    // load, so the textarea reads consistently and the next Save persists it.
    // Deliberately NOT mutating the shared model — an unrelated save of this
    // bloodwork elsewhere shouldn't quietly rewrite the practitioner's note.
    initBloodworkNote(section: string, bloodwork: Bloodwork): BloodworkNote {
        if (bloodwork && bloodwork.bloodworkNotes) {
            let bloodworkNote = bloodwork.bloodworkNotes.findNote(section);
            if (!bloodworkNote) {
                bloodworkNote = bloodwork.bloodworkNotes.addNote(section, '');
            }
            return new BloodworkNote({
                section: bloodworkNote.section,
                text: trianglesToArrows(bloodworkNote.text),
            });
        }
        return new BloodworkNote();
    }

    setWrapperRef = (el: HTMLDivElement | null) => {
        this.wrapperEl = el;
    }

    setRootRef = (el: HTMLDivElement | null) => {
        this.rootEl = el;
    }

    fitToContent = () => {
        const el = this.wrapperEl?.querySelector('textarea') as HTMLTextAreaElement | null;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, BloodworkNotes.MIN_HEIGHT_PX)}px`;
        el.style.overflow = 'hidden';
    }

    handleInputChange = (event: any) => {
        const { section } = this.props;
        const value = event.target.value;
        // Grow with the text as it's typed — the box should never need dragging
        // (or a bigger window) to read what's in it.
        this.setState({
            bloodworkNote: new BloodworkNote({section, text: value}),
        }, this.fitToContent);
    }

    saveOrUpdate = (event: any) => {
        const { section, bloodwork, bloodworkEntryStore } = this.props;
        event.preventDefault();
        if (!bloodwork || !bloodwork.bloodworkNotes) return;
        // findNote can be undefined if this section's note was never created;
        // create it rather than assigning .text on undefined.
        let bloodworkNote = bloodwork.bloodworkNotes.findNote(section);
        if (!bloodworkNote) {
            bloodworkNote = bloodwork.bloodworkNotes.addNote(section, '');
        }
        bloodworkNote.text = this.state.bloodworkNote.text;
        bloodworkEntryStore.saveBloodwork(bloodwork)
            .then(this.scrollBackToNotes)
            .catch((err) => {
                console.error('Failed to save bloodwork note', err);
                alert('Failed to save your note. Please try again.');
                this.scrollBackToNotes();
            });
        // Back to the formatted preview, so Save doubles as "show me how this
        // reads". Editing again is one click away.
        this.setState({ editing: false });
    }

    // Saving runs through the app's spinner modal, which locks body scrolling —
    // anything scrolled underneath it is clamped, and the page lands at the top
    // of the report when it closes. Scroll back once the save has resolved, to
    // the enclosing card when the caller named one (so its header shows) and
    // otherwise to the top of this field.
    private scrollBackToNotes = () => {
        scrollPanelIntoView(this.props.scrollAnchorId || this.rootEl);
    }

    handleEdit = () => {
        this.setState({ editing: true }, this.fitToContent);
    }

    // Discard anything typed since Edit by re-reading the saved note.
    handleCancel = () => {
        const { section, bloodwork } = this.props;
        this.setState({
            bloodworkNote: this.initBloodworkNote(section, bloodwork),
            editing: false,
        });
    }

    // ---- "gather from tabs" digest wiring -------------------------------

    private digestTabs(): DigestTab[] {
        const { section, bloodworkAnalysis, furtherConsiderations, panelMarkers } = this.props;
        if (!bloodworkAnalysis || !furtherConsiderations) return [];
        return buildSectionDigest(section, bloodworkAnalysis, furtherConsiderations, panelMarkers || []);
    }

    // Append with a blank line before the block when the note isn't already
    // separated, so inserted tabs read as their own paragraphs.
    private static appendBlock(existing: string, block: string): string {
        let v = existing || '';
        if (v && !v.endsWith('\n\n')) v += v.endsWith('\n') ? '\n' : '\n\n';
        return v + block;
    }

    // Index of the LAST occurrence of a block, or -1. Blocks now carry ↑/↓
    // arrows, but notes saved before that conversion hold the DB's ▲/▼
    // triangles, so fall back to the legacy form — otherwise those tabs would
    // read as "not added" and Insert would append a second copy. The swap is
    // character-for-character, so the index and block.length still line up
    // against the original text either way.
    private static findBlock(existing: string, block: string): number {
        const idx = existing.lastIndexOf(block);
        if (idx !== -1) return idx;
        return existing.lastIndexOf(arrowsToTriangles(block));
    }

    // Remove the LAST occurrence of a block plus the blank-line separator we
    // appended after it, leaving the rest of the note's spacing untouched.
    // (Last, not first, so we never delete an identical block the practitioner
    // pasted earlier; scoped to the seam so we never reflow their own text.)
    private static removeBlock(existing: string, block: string): string {
        const idx = BloodworkNotes.findBlock(existing, block);
        if (idx === -1) return existing;
        const before = existing.slice(0, idx);
        const after = existing.slice(idx + block.length).replace(/^\n\n?/, '');
        return before + after;
    }

    // A tab counts as "inserted" when the note currently contains its exact
    // block. Deriving this from the note text (rather than tracking separate
    // state) keeps the Insert⇄Added toggle correct across Save and page reload,
    // so re-inserting can't silently append a duplicate.
    private isInserted(tab: DigestTab): boolean {
        return BloodworkNotes.findBlock(
            this.state.bloodworkNote.text || '',
            digestTabToMarkdown(tab),
        ) !== -1;
    }

    // Every digest insert path (tab toggle, whole section, single line, Insert
    // all) routes through here, so this is the one place that has to open the
    // editor: the digest is visible in preview mode too, and text inserted from
    // it lives in local state until Save. Landing in the editor puts the Save
    // button on screen with the newly inserted block, instead of silently
    // staging a change with no way to keep it.
    private setNoteText = (text: string) => {
        const { section } = this.props;
        this.setState(
            { bloodworkNote: new BloodworkNote({ section, text }), editing: true },
            this.fitToContent,
        );
    };

    // ---- rich-text (inline markdown) formatting ------------------------

    private getTextarea = (): HTMLTextAreaElement | null =>
        (this.wrapperEl?.querySelector('textarea') as HTMLTextAreaElement | null) ?? null;

    // Set the note text, then restore the selection the toolbar/shortcut asked
    // for once React has re-rendered the (controlled) textarea.
    private applyFormat = (value: string, selStart: number, selEnd: number) => {
        const { section } = this.props;
        this.setState(
            { bloodworkNote: new BloodworkNote({ section, text: value }) },
            () => {
                this.fitToContent();
                const el = this.getTextarea();
                if (el) {
                    requestAnimationFrame(() => {
                        el.focus();
                        el.setSelectionRange(selStart, selEnd);
                    });
                }
            },
        );
    };

    private wrapWith = (marker: FormatMarker) => {
        const el = this.getTextarea();
        if (!el) return;
        const { value, selStart, selEnd } = wrapSelection(el, marker);
        this.applyFormat(value, selStart, selEnd);
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const marker = formatMarkerForKeydown(e);
        if (marker) {
            e.preventDefault();
            this.wrapWith(marker);
        }
    };

    private handleToggleTab = (tab: DigestTab) => {
        const block = digestTabToMarkdown(tab);
        const current = this.state.bloodworkNote.text || '';
        const nextText = BloodworkNotes.findBlock(current, block) !== -1
            ? BloodworkNotes.removeBlock(current, block)
            : BloodworkNotes.appendBlock(current, `${block}\n\n`);
        this.setNoteText(nextText);
    };

    // Insert a whole section (its bold heading + every bullet). Triangles become
    // ↑/↓ here for the same reason they do in the digest's tab blocks — the
    // textarea holds text, not the tab's FontAwesome arrow icons.
    private handleInsertGroup = (heading: string, items: string[]) => {
        const current = this.state.bloodworkNote.text || '';
        const bullets = items.map(i => `• ${trianglesToArrows(i)}`).join('\n');
        const block = `**${trianglesToArrows(heading)}**\n${bullets}\n\n`;
        this.setNoteText(BloodworkNotes.appendBlock(current, block));
    };

    // True when the most recent bold heading in the note is exactly `headingLine`
    // — so adding several bullets from one section stacks them under a single
    // heading instead of repeating it. Compared in triangle form so a heading
    // inserted before the arrow conversion still matches its arrowed self.
    private static lastHeadingMatches(text: string, headingLine: string): boolean {
        const target = arrowsToTriangles(headingLine);
        const lines = text.split('\n');
        for (let i = lines.length - 1; i >= 0; i--) {
            const t = lines[i].trim();
            if (/^\*\*.+\*\*$/.test(t)) return arrowsToTriangles(t) === target;
        }
        return false;
    }

    // Insert one bullet, carrying its section heading so the note keeps context.
    // The heading is added only when it isn't already the current section.
    private handleInsertLine = (item: string, heading: string) => {
        const current = this.state.bloodworkNote.text || '';
        const headingLine = `**${trianglesToArrows(heading)}**`;
        const bullet = `• ${trianglesToArrows(item)}`;
        if (BloodworkNotes.lastHeadingMatches(current, headingLine)) {
            let v = current;
            if (v && !v.endsWith('\n')) v += '\n';
            this.setNoteText(`${v}${bullet}\n`);
        } else {
            this.setNoteText(
                BloodworkNotes.appendBlock(current, `${headingLine}\n${bullet}\n`),
            );
        }
    };

    private handleInsertAll = () => {
        let text = this.state.bloodworkNote.text || '';
        this.digestTabs().forEach(tab => {
            const block = digestTabToMarkdown(tab);
            if (BloodworkNotes.findBlock(text, block) !== -1) return;
            text = BloodworkNotes.appendBlock(text, `${block}\n\n`);
        });
        this.setNoteText(text);
    };

    // The digest belongs under the note in BOTH modes. It is a read-only view of
    // what this section's other tabs already say, so gating it on Edit meant a
    // section with a saved note showed nothing to pull from until the
    // practitioner clicked into the editor — the exact moment they least need
    // to be hunting for the button. Renders nothing when the section has no
    // gathered content, or when the caller omitted the analysis props (the
    // global Insights note).
    private renderDigest() {
        const { bloodworkAnalysis, furtherConsiderations } = this.props;
        if (!bloodworkAnalysis || !furtherConsiderations) return null;
        const tabs = this.digestTabs();
        if (!tabs.length) return null;
        const insertedKeys = tabs.filter(t => this.isInserted(t)).map(t => t.key);
        return (
            <SectionNotesDigest
                tabs={tabs}
                insertedKeys={insertedKeys}
                onToggleTab={this.handleToggleTab}
                onInsertGroup={this.handleInsertGroup}
                onInsertLine={this.handleInsertLine}
                onInsertAll={this.handleInsertAll}
            />
        );
    }

    render() {
        const { section, title } = this.props;
        const { editing } = this.state;
        const text = this.state.bloodworkNote.text ?? '';

        if (!editing) {
            return (
                <div ref={this.setRootRef}>
                    {/* Grows with the note — no inner scrollbar, nothing to drag
                        open to read what was written. */}
                    <div
                        style={{
                            border: '1px solid #ced4da',
                            borderRadius: 4,
                            padding: 12,
                            backgroundColor: '#fff',
                        }}
                    >
                        {text.trim()
                            ? renderNoteBody(text)
                            : <span style={{ color: '#6c757d' }}>No notes yet.</span>}
                    </div>
                    <div style={{ marginTop: 10 }}>
                        <Button type="button" onClick={this.handleEdit} title="Edit these notes">
                            Edit
                        </Button>
                    </div>
                    {this.renderDigest()}
                </div>
            );
        }

        return (
            <div ref={this.setRootRef}>
                <Form id="notes-form">
                    <Form.Group controlId="exampleForm.ControlTextarea1">
                        <Form.Label>{title}</Form.Label>
                        <MarkdownToolbar
                            getTextarea={this.getTextarea}
                            onApply={this.applyFormat}
                        />
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 6 }}>
                            Formatting:&nbsp;<code>## Section</code>&nbsp;for section headings,&nbsp;
                            <code>**bold**</code>,&nbsp;<code>*italic*</code>,&nbsp;<code>_underline_</code>.
                            {/* Only a body-system section's note feeds the
                                consolidated Section Notes panel — the global
                                Insights note has no section to be gathered
                                under, so don't promise it there. */}
                            &nbsp;These render formatted when you Save
                            {section === Section.NOTES
                                ? '.'
                                : ' — here and in the Section Notes summary.'}
                        </div>
                        <div ref={this.setWrapperRef}>
                            <Form.Control as="textarea" rows="6"
                                name="notes-text"
                                value={text}
                                onChange={this.handleInputChange}
                                onKeyDown={this.handleKeyDown}
                                style={{ overflow: 'hidden', resize: 'none' }}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Col style={{ display: 'flex', gap: 8 }}>
                            <Button id="submitButton" type="submit" onClick={this.saveOrUpdate}>Save</Button>
                            <Button type="button" variant="secondary" onClick={this.handleCancel}>Cancel</Button>
                        </Col>
                    </Form.Group>
                </Form>
                {this.renderDigest()}
            </div>
        );
    }
}